import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, TextField, MenuItem,
  Grid, Chip, InputAdornment, Tooltip, Drawer, Divider, Stack, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';

import Header from '../../components/Header';
import { supabase } from '../../lib/supabaseClient';

function FacilityDrawer({ open, onClose, initial, onSaved, onDeleted, orgId }) {
  const editing = Boolean(initial?.id);
  const [form, setForm] = useState({
    name: '', npi: '', address: '', city: '', state: '', zip: '', country: 'US', phone: '', fax: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name ?? '', npi: initial?.npi ?? '', address: initial?.address ?? '',
        city: initial?.city ?? '', state: initial?.state ?? '', zip: initial?.zip ?? '',
        country: initial?.country ?? 'US', phone: initial?.phone ?? '', fax: initial?.fax ?? '',
      });
      setSaving(false);
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Name is required.');
    if (!editing && !orgId) return alert('Select an organization first.');
    setSaving(true);
    try {
      const payload = {
        name: form.name || null, npi: form.npi || null, address: form.address || null, city: form.city || null,
        state: form.state || null, zip: form.zip || null, country: form.country || null,
        phone: form.phone || null, fax: form.fax || null,
      };
      if (editing) {
        const { error } = await supabase.from('facilities').update(payload).eq('id', initial.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('facilities').insert([{ ...payload, org_id: orgId }]);
        if (error) throw error;
      }
      onSaved?.(); onClose();
    } catch (e) {
      alert(`Save failed: ${e.message}`); setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm('Delete this facility? This cannot be undone.')) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('facilities').delete().eq('id', initial.id);
      if (error) throw error;
      onDeleted?.(); onClose();
    } catch (e) {
      alert(`Delete failed: ${e.message}`); setSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 420 } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
        <Typography variant="h6">{editing ? 'Edit Facility' : 'Add Facility'}</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Divider />
      <Box p={2} component="form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <Stack spacing={2}>
          <TextField label="Name*" value={form.name} onChange={set('name')} fullWidth autoFocus />
          <TextField label="NPI (10 digits)" value={form.npi} onChange={set('npi')} fullWidth />
          <TextField label="Address" value={form.address} onChange={set('address')} fullWidth />
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField label="City" value={form.city} onChange={set('city')} fullWidth /></Grid>
            <Grid item xs={3}><TextField label="State" value={form.state} onChange={set('state')} fullWidth /></Grid>
            <Grid item xs={3}><TextField label="ZIP" value={form.zip} onChange={set('zip')} fullWidth /></Grid>
          </Grid>
          <TextField label="Country" value={form.country} onChange={set('country')} fullWidth />
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField label="Phone" value={form.phone} onChange={set('phone')} fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Fax" value={form.fax} onChange={set('fax')} fullWidth /></Grid>
          </Grid>

          <Stack direction="row" spacing={1} justifyContent="flex-end" pt={1}>
            {editing && (
              <Button startIcon={<DeleteOutlineIcon />} color="error" variant="outlined" onClick={handleDelete}>
                Delete
              </Button>
            )}
            <Button startIcon={<SaveIcon />} variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}

export default function Facilities() {
  const theme = useTheme();
  const navigate = useNavigate();
  const go = (path, facilityId) => {
    navigate(`${path}?org=${orgId || ''}&facility=${facilityId}`);
  };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const [userId, setUserId] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(null);

  useEffect(() => { (async () => { const { data, error } = await supabase.auth.getUser(); if (!error && data?.user?.id) setUserId(data.user.id); })(); }, []);

  useEffect(() => {
    (async () => {
      if (!userId) { setLoading(false); return; }
      setLoading(true);
      try {
        const { data: mems } = await supabase.from('organization_members').select('org_id').eq('user_id', userId);
        const ids = (mems || []).map((m) => m.org_id);
        if (!ids.length) { setOrgs([]); setOrgId(null); return; }
        const { data: orgRows } = await supabase.from('organizations').select('id,name').in('id', ids);
        const list = (orgRows || []).map((o) => ({ id: o.id, name: o.name || o.id }));
        setOrgs(list);
        setOrgId((prev) => prev || list[0]?.id || null);
      } catch (e) {
        console.error('Org load failed:', e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const loadRows = async () => {
    if (!orgId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('facilities')
        .select('id, org_id, name, npi, address, city, state, zip, country, phone, fax, created_at')
        .eq('org_id', orgId)
        .order('name', { ascending: true });
      if (error) throw error;

      const facilities = (data || []).map((r) => ({
        id: r.id,
        org_id: r.org_id,
        name: r.name || '—',
        npi: r.npi || '—',
        address: r.address || '—',
        city: r.city || '',
        state: r.state || '',
        zip: r.zip || '',
        country: r.country || '',
        phone: r.phone ?? '—',
        fax: r.fax ?? '—',
        locationDisplay: [r.city, r.state].filter(Boolean).join(', ') || '—',
        _state: r.state || '—',
        _country: r.country || '—',
        created_at: r.created_at,
      }));

      const { data: pats } = await supabase.from('patients').select('facility_id').eq('org_id', orgId);
      const pCounts = {}; (pats || []).forEach((p) => { if (p.facility_id) pCounts[p.facility_id] = (pCounts[p.facility_id] || 0) + 1; });

      const { data: provs } = await supabase.from('providers').select('facility_id').eq('org_id', orgId);
      const provCounts = {}; (provs || []).forEach((pr) => { if (pr.facility_id) provCounts[pr.facility_id] = (provCounts[pr.facility_id] || 0) + 1; });

      const { data: reps } = await supabase.from('lab_reports').select('facility_id').eq('org_id', orgId);
      const rCounts = {}; (reps || []).forEach((r) => { if (r.facility_id) rCounts[r.facility_id] = (rCounts[r.facility_id] || 0) + 1; });

      const rowsWithCounts = facilities.map((f) => ({
        ...f,
        providers: provCounts[f.id] || 0,
        patients: pCounts[f.id] || 0,
        lab_results: rCounts[f.id] || 0,
      }));

      setRows(rowsWithCounts);
    } catch (e) {
      alert(`Failed to load facilities: ${e.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRows(); /* eslint-disable-next-line */ }, [orgId]);

  const stateOptions = useMemo(() => ['all', ...Array.from(new Set(rows.map((r) => r._state).filter((s) => s && s !== '—'))).sort()], [rows]);
  const countryOptions = useMemo(() => ['all', ...Array.from(new Set(rows.map((r) => r._country).filter((c) => c && c !== '—'))).sort()], [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (stateFilter !== 'all' && r._state !== stateFilter) return false;
      if (countryFilter !== 'all' && r._country !== countryFilter) return false;
      if (!q) return true;
      return [
        r.name, r.npi, r.address, r.city, r.state, r.zip, r.country, String(r.phone), String(r.fax),
      ].join(' ').toLowerCase().includes(q);
    });
  }, [rows, search, stateFilter, countryFilter]);

  const columns = [
    {
      field: 'menu', headerName: '', width: 48, sortable: false,
      renderCell: (p) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => {
              const row = rows.find((r) => r.id === p.row.id);
              setEditingRow(row || null);
              setDrawerOpen(true);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    { field: 'name', headerName: 'Name', flex: 1.2, minWidth: 180 },
    { field: 'npi', headerName: 'NPI', width: 140 },
    { field: 'address', headerName: 'Address', flex: 1.2, minWidth: 220 },
    { field: 'locationDisplay', headerName: 'Location', flex: 1, minWidth: 180 },
    { field: 'phone', headerName: 'Phone', width: 140 },
    {
      field: 'providers', headerName: 'Providers', width: 110,
      renderCell: ({ row }) => (
        <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/providers?org=${orgId || ''}&facility=${row.id}`); }}>
          {row?.providers ?? 0}
        </Button>
      ),
    },
    {
      field: 'patients', headerName: 'Patients', width: 110,
      renderCell: ({ row }) => (
        <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/patients?org=${orgId || ''}&facility=${row.id}`); }}>
          {row?.patients ?? 0}
        </Button>
      ),
    },
    {
      field: 'lab_results', headerName: 'Lab Reports', width: 120,
      renderCell: ({ row }) => (
        <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/lab-results?org=${orgId || ''}&facility=${row.id}`); }}>
          {row?.lab_results ?? 0}
        </Button>
      ),
    },
  ];

  return (
    <Box p={3} sx={{ pb: 6 }}>
      {/* Header + actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Header title="Facilities" subtitle="" />
        <Box display="flex" gap={1}>
          {orgs.length > 0 && (
            <TextField select size="small" label="Organization" value={orgId || ''} onChange={(e) => setOrgId(e.target.value)} sx={{ minWidth: 220 }}>
              {orgs.map((o) => (<MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>))}
            </TextField>
          )}
          <TextField
            size="small" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
            sx={{ minWidth: 280 }}
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows} disabled={!orgId}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingRow(null); setDrawerOpen(true); }} disabled={!orgId}>New</Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField select label="State" size="small" fullWidth value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
                {stateOptions.map((s) => (<MenuItem key={s} value={s}>{s === 'all' ? 'All' : s}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select label="Country" size="small" fullWidth value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
                {countryOptions.map((c) => (<MenuItem key={c} value={c}>{c === 'all' ? 'All' : c}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} display="flex" alignItems="center">
              <Chip size="small" variant="outlined" label="Counts derive from patients/reports tables" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          <Box sx={{ height: 620, width: '100%' }}>
            <DataGrid
              rows={filtered}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              getRowHeight={() => 52}
              onRowDoubleClick={(params) => { const row = rows.find((r) => r.id === params.row.id); setEditingRow(row || null); setDrawerOpen(true); }}
              sx={{ '& .MuiDataGrid-columnHeaders': { background: theme.palette.background.paper } }}
            />
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Typography variant="caption" color="text.secondary">Rows per page: 100 • {filtered.length} results</Typography>
          </Box>
        </CardContent>
      </Card>

      <FacilityDrawer open={drawerOpen} initial={editingRow} onClose={() => setDrawerOpen(false)} onSaved={loadRows} onDeleted={loadRows} orgId={orgId} />
    </Box>
  );
}
